import { BonusType } from '@superbet-group/web.types';

import { logger } from '@app/logger';
import Balance from '@models/user/Balance';
import {
    BonusTypeComtrade,
    BonusTypeDto,
    isComtradeBonusType,
} from '@core/models/bonus/BonusTypeDto';
import { BonusBalanceData } from '@models/user/BonusBalanceData';

import {
    AccountDto,
    AccountType,
    BalanceDto,
    BonusCategoryDto,
    BonusSubCategory,
} from './dtos/BalanceDto';
import ProductTypeDto from '@core/mappers/bonus/dtos/ProductTypeDto';

export function transformBalance(dto: BalanceDto): Balance | null {
    if (dto.Accounts.length === 0) {
        return null;
    }
    const balance: Balance = {
        realMoney: {
            total: 0,
        },
        bonuses: emptyBonusBalanceData(),
    };

    dto.Accounts.forEach((account) => {
        if (account.AccountType === AccountType.Regular) {
            // We know there's only one account with the regular type representing the user's real balance.
            balance.realMoney.total = account.Balance;
        } else if (account.AccountType === AccountType.Bonus && isActiveBonusBalance(account)) {
            // Since the user can only have one active bonus per type at a time, we can be sure that
            // the next line can only return a certain type once per iteration (i.e., no values will be overwritten).
            try {
                const type = getBonusTypeForBalance(account.BonusType, account.ProductType, account.BonusCategoryId);
                balance.bonuses[type].amount = account.Balance;
                balance.bonuses[type].id = account.PlayerBonusId || null;
            } catch {
                // do nothing
            }
        }
    });

    return balance;
}

export function emptyBonusBalanceData(): BonusBalanceData {
    return Object.values(BonusType).reduce(
        (result, type) => ({
            ...result,
            [type]: {
                id: null,
                amount: null,
            },
        }),
        {},
    ) as BonusBalanceData;
}

function isActiveBonusBalance(account: AccountDto) {
    // Active bonuses must have a balance greater than 0.
    return account.Balance > 0;
}

export function getBonusTypeForBalance(bonusType: BonusTypeDto, productType: ProductTypeDto, bonusCategory?: BonusCategoryDto): BonusType {
    const type = getBonusType(bonusType, productType);
    if (bonusCategory && type === BonusType.Sport) {
        const bonusCategoryType = typeCategoryMappings[bonusCategory];
        if (!bonusCategoryType) {
            logger.error(new Error(`Undefined type for bonusCategory type combination: ${bonusCategory}`));
        }
        return bonusCategoryType;
    }

    return type;
}

const typeCategoryMappings: Record<BonusCategoryDto, BonusType> = {
    [BonusSubCategory.FirstDeposit]: BonusType.Sport,
    [BonusSubCategory.Reload]: BonusType.Sport,
    [BonusSubCategory.Rebate]: BonusType.Sport,
    [BonusSubCategory.Cashback]: BonusType.Sport,
    [BonusSubCategory.FreeSpins]: BonusType.Sport,
    [BonusSubCategory.AwardGames]: BonusType.Sport,
    [BonusSubCategory.FreeBet]: BonusType.Freebet,
};

export function getBonusType(bonusType: BonusTypeDto, productType: ProductTypeDto): BonusType {
    if (isComtradeBonusType(bonusType) && typeMappings[bonusType][productType]) {
        return typeMappings[bonusType][productType]!;
    } else {
        throw new Error(`Undefined type for bonus and product type combination: ${bonusType}, ${productType} `);
    }
}

const typeMappings: Record<BonusTypeComtrade, Partial<Record<ProductTypeDto, BonusType>>> = {
    [BonusTypeComtrade.Casino]: {
        [ProductTypeDto.Casino]: BonusType.Casino,
        [ProductTypeDto.CasinoVirtuals]: BonusType.CasinoVirtuals,
    },
    [BonusTypeComtrade.External]: {
        [ProductTypeDto.Casino]: BonusType.Casino,
    },
    [BonusTypeComtrade.Sportsbook]: {
        [ProductTypeDto.Sportsbook]: BonusType.Sport,
        [ProductTypeDto.Virtuals]: BonusType.Virtuals,
    },
};
